import { Component, input, inject, viewChild, signal, computed, PLATFORM_ID, AfterViewInit } from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatTableModule, MatTable } from '@angular/material/table';
import { MatSelectModule, MatSelectChange } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AlternativeToolForm, createAlternativeToolGroup } from '../../utilities/helpers/tool-form.forms';
import { RELATIONSHIP_TYPE_OPTIONS } from 'src/app/features/tooling/models/tool-form-types';
import { ensureAtLeastOne } from '../../utilities/helpers/form-array-helper';

@Component({
  selector: 'alternative-tools-table',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatTableModule, MatSelectModule, MatFormFieldModule],
  templateUrl: './alternative-tools-table.component.html',
  styleUrls: ['./alternative-tools-table.component.scss']
})
export class AlternativeToolsTableComponent implements AfterViewInit {
  private readonly fb = inject(FormBuilder);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  alternativeTools = input.required<FormArray<FormGroup<AlternativeToolForm>>>();
  toolOptions = input.required<Array<{ id: number; name: string }>>();
  toolOptionsLoading = input.required<boolean>();
  toolOptionsError = input.required<string | null>();

  readonly table = viewChild<MatTable<unknown>>('altTable');

  readonly displayedColumns = ['name', 'relationshipType', 'actions'];
  readonly relationshipOptions = RELATIONSHIP_TYPE_OPTIONS;

  readonly alternativeToolFilter = signal('');
  readonly alternativeToolSearchValue = '__search__';
  private readonly lastAlternativeToolSelectionByGroup = new Map<FormGroup<AlternativeToolForm>, number | null>();

  readonly filteredToolOptions = computed(() => {
    const term = this.alternativeToolFilter().trim().toLowerCase();
    const all = this.toolOptions();
    if (!term) return all;
    return all.filter(o => (o.name ?? '').toLowerCase().includes(term));
  });

  onSelectOpened(row: FormGroup<AlternativeToolForm>, opened: boolean): void {
    if (!opened) return;

    // Track the last valid selection so we can revert if the user hits the search row.
    const current = (row.get('relatedToolId')?.value ?? null) as number | null;
    this.lastAlternativeToolSelectionByGroup.set(row, current);

    this.alternativeToolFilter.set('');

    // Focus the in-panel search input after the overlay renders.
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        const el = this.document.querySelector('.mat-mdc-select-panel .select-search-input') as HTMLInputElement | null;
        el?.focus();
        el?.select();
      });
    }
  }

  onSelectionChange(row: FormGroup<AlternativeToolForm>, event: MatSelectChange): void {
    if (event.value === this.alternativeToolSearchValue) {
      const last = this.lastAlternativeToolSelectionByGroup.get(row) ?? null;
      row.get('relatedToolId')?.setValue(last, { emitEvent: false });

      // Keep the panel open and return focus to the input.
      if (isPlatformBrowser(this.platformId)) {
        setTimeout(() => {
          event.source.open();
          const el = this.document.querySelector('.mat-mdc-select-panel .select-search-input') as HTMLInputElement | null;
          el?.focus();
          el?.select();
        });
      }
      return;
    }

    this.lastAlternativeToolSelectionByGroup.set(row, (event.value ?? null) as number | null);
  }

  onFilterInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.alternativeToolFilter.set(value);
  }

  addTool(): void {
    this.alternativeTools().push(createAlternativeToolGroup(this.fb));
    this.renderTable();
  }

  removeTool(index: number): void {
    this.alternativeTools().removeAt(index);
    ensureAtLeastOne(this.alternativeTools(), () => createAlternativeToolGroup(this.fb));
    this.renderTable();
  }

  private renderTable(): void {
    this.table()?.renderRows();
  }

  ngAfterViewInit(): void {
    this.renderTable();

    const arr = this.alternativeTools();
    arr?.valueChanges?.subscribe?.(() => this.renderTable());
  }
}
